/**
 * Global state for tracking draft and pending chat messages.
 *
 * This allows:
 * - Preserving partially authored messages across component unmount/remount
 * - Showing draft indicators in the chat listing
 * - Tracking file uploads through their lifecycle (uploading -> completed, removed on failure)
 *
 * State is keyed by chatSessionId for existing chats, or NEW_CHAT_DRAFT_KEY for new chats.
 */

import {useMemo} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {AgentType} from 'graphql-client/__generated/graphqlGlobalTypes';
import localStorage from 'LocalStorage';
import preferences from 'Preferences';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';

import {
  AgentApprovalDecisionInput,
  CitationGenerationMode,
  ChatSourceInput,
  CreateDisplayMessageChatEvent,
  getContextIdsFromActiveWorkspace,
  getUserChatContext,
} from './ChatDAO';
import {getCompletedUploads} from './ChatFileHelper';
import {ChatSelectedFile} from './ChatLibraryTypes';
import {
  doesAgentTypeSupportPublicData,
  doesAgentTypeSupportSources,
  getDefaultAgentType,
} from './sidepanel/ChatTypeUtil';

/** Sentinel key for tracking drafts in new chats that haven't been persisted yet. */
export const NEW_CHAT_DRAFT_KEY = '__NEW_CHAT__';

export enum ChatDraftStatus {
  /** User is composing a message but hasn't sent it yet. */
  DRAFT = 'DRAFT',
  /** Message has been submitted and is awaiting server response. */
  PENDING = 'PENDING',
}

const PUBLIC_DATA_ENABLED_PREFERENCE_KEY = 'llm.chat.publicDataEnabled';

/**
 * Whether AWS-backed web search defaults on for new chats. This is stored as a user preference so
 * it follows the user across browsers and devices. It was briefly persisted in localStorage under
 * the same key, so reads fall back to that legacy localStorage value for back-compat.
 */
const WEB_SEARCH_ENABLED_PREFERENCE_KEY = 'llm.chat.webSearchEnabled';

function getStoredWebSearchEnabledPreference(): boolean | null {
  // The user preference takes precedence; fall back to the legacy localStorage value for
  // back-compat with sessions that toggled web search before it became a user preference.
  const userPreference = preferences.get(WEB_SEARCH_ENABLED_PREFERENCE_KEY);
  if (typeof userPreference === 'boolean') {
    return userPreference;
  }
  return getLegacyLocalStorageWebSearchEnabledPreference();
}

function getLegacyLocalStorageWebSearchEnabledPreference(): boolean | null {
  try {
    const storedPreference = localStorage.getItem(WEB_SEARCH_ENABLED_PREFERENCE_KEY);
    if (storedPreference === 'true') {
      return true;
    }
    if (storedPreference === 'false') {
      return false;
    }
    return null;
    // Preference persistence is best effort because some browser storage modes throw on access.
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch {
    return null;
  }
}

export function getPublicDataEnabledPreference(): boolean {
  if (!BenchlingConfig.LLM_ENABLE_AWS_WEB_SEARCH) {
    try {
      return localStorage.getItem(PUBLIC_DATA_ENABLED_PREFERENCE_KEY) === 'true';
      // Preference persistence is best effort because some browser storage modes throw on access.
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch {
      return false;
    }
  }
  return getStoredWebSearchEnabledPreference() ?? true;
}

export function setPublicDataEnabledPreference(publicDataEnabled: boolean): void {
  if (BenchlingConfig.LLM_ENABLE_AWS_WEB_SEARCH) {
    preferences.set(WEB_SEARCH_ENABLED_PREFERENCE_KEY, publicDataEnabled);
    return;
  }
  try {
    localStorage.setItem(PUBLIC_DATA_ENABLED_PREFERENCE_KEY, String(publicDataEnabled));
    // Preference persistence is best effort because some browser storage modes throw on access.
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch {
    // No-op.
  }
}

export interface ChatDraftMessage {
  text: string;
  /** Files in the draft (uploading or completed) */
  selectedFiles: Array<ChatSelectedFile>;
}

const EMPTY_MESSAGE: ChatDraftMessage = {
  text: '',
  selectedFiles: [],
};

/**
 * Settings for a chat draft. When undefined on ChatDraftState, indicates the settings
 * are not yet known (e.g. for an existing chat that hasn't loaded yet).
 */
export interface ChatDraftSettings {
  agentType: AgentType;
  /**
   * TODO: This should be replaced by sources: `Array<{type: SOURCE_TYPE}>`.
   * For now, we use `getSources` to translate to the expected shape for the API.
   */
  publicDataEnabled: boolean;
  /** Assistant report citation behavior. Defaults to AUTO. */
  citationGenerationMode?: CitationGenerationMode;
  /**
   * MCP server IDs selected for this chat. When undefined, all enabled servers are used
   * (the default until the user explicitly customizes the selection).
   */
  selectedMcpServerIds?: Array<string>;
  /**
   * Whether the user has requested write-action approval for the chat this draft will create.
   *
   * Only applies to new chats (chatSessionId is null): rather than granting approval
   * immediately (which would require creating the chat as a side effect of clicking the plus
   * menu), we hold the intent here and send it as part of creating the chat when the initial
   * message is submitted. Existing chats instead grant/revoke approval directly via
   * useMessageThread's grantFullAccess/revokeFullAccess, which call the server.
   */
  newChatFullAccess?: boolean;
}

export interface AgentTypeError {
  title: string;
  detail: string;
}

/**
 * Catalog use case a new-chat draft was opened from, so a later composer send can be counted as
 * that URL run. `campaign` is the marketing campaign on the landing URL when one was present.
 */
export interface StagedChatUseCase {
  chatUseCaseId: string;
  categoryId: string;
  campaign: string | null;
}

export interface ChatDraftState {
  status: ChatDraftStatus;
  message: ChatDraftMessage;
  /**
   * Failed submitted message rendered in-thread while the input returns to editable draft mode.
   * This primarily exists to avoid jarring UI where an optimistic message and the spacing below it is
   * removed when submitting the message fails. This is opt-in behavior when handling a server error,
   * e.g. see handleInsufficientCredits in MeteringUtils.tsx.
   */
  failedUserMessage?: ChatDraftMessage;
  /** Undefined indicates settings are not yet known (e.g. for an existing chat, not yet loaded). */
  settings: ChatDraftSettings | undefined;
  agentTypeError?: AgentTypeError;
  /**
   * The ID of the last display message (user or assistant) at the time this pending message was submitted.
   * Used to detect when the optimistic message has been persisted - we clear it when
   * a new user message appears after this position. Set when status transitions to PENDING.
   * Null for DRAFT status or for the first message in a chat.
   */
  replyToId?: string | null;
  /**
   * If this pending message is a retry, the ID of the original user message being retried.
   * Used to filter out the original message from display when showing the retry.
   * Null when not a retry.
   */
  retryOfId?: string | null;
  /**
   * Catalog use case this new-chat draft was opened from. A composer send of this draft is that
   * use case's run, attributed to the same campaign as the landing URL when one was present.
   */
  stagedChatUseCase?: StagedChatUseCase;
}

/**
 * Create a fresh draft state for a new chat with the given agent type and optional initial prompt.
 * When agentType is null (no agents available), settings is left undefined.
 */
export function makeNewDraftState(
  agentType: AgentType | null,
  initialPrompt: string = '',
  options: {stagedChatUseCase?: StagedChatUseCase} = {}
): ChatDraftState {
  return {
    status: ChatDraftStatus.DRAFT,
    message: {
      ...EMPTY_MESSAGE,
      text: initialPrompt,
    },
    failedUserMessage: undefined,
    settings: agentType ? {agentType, publicDataEnabled: getPublicDataEnabledPreference()} : undefined,
    agentTypeError: undefined,
    ...(options.stagedChatUseCase != null ? {stagedChatUseCase: options.stagedChatUseCase} : {}),
  };
}

/**
 * Options for submitting a draft message.
 */
export interface CreateParams {
  /** Context IDs to include (file IDs, etc). Defaults to active workspace context. */
  contextIds?: Array<string>;
  /** Whether this is a retry of a failed message. */
  retry?: boolean;
  /** MCP server IDs to include as sources. Populated from MCPEnabledServersContext by callers. */
  mcpServerIds?: ReadonlyArray<string>;
  /**
   * The user's agent-approval decision carried by this message (see LLM_ENABLE_AGENT_APPROVALS): the
   * approval card's buttons send APPROVED_THIS_CHAT ("Allow") or EXPLICITLY_DENIED ("Deny"), while a
   * typed reply sent while a request is pending sends IMPLICITLY_DENIED. Omit when the message
   * answers no request. Build it with ``buildAgentApprovalDecision``.
   */
  agentApprovalDecision?: AgentApprovalDecisionInput;
}

// ============================================
// Conversion Helpers
// ============================================

/**
 * Derive sources from settings.
 *
 * MCP server IDs come from settings.selectedMcpServerIds (per-chat selection).
 * When undefined, falls back to mcpServerIds from createParams (all enabled servers).
 *
 * TODO: ChatDraftState should replace publicDataEnabled with sources directly,
 * and the UI must be able to handle sources more generally.
 */
function getSources(
  settings: ChatDraftSettings,
  fallbackMcpServerIds: ReadonlyArray<string>
): Array<ChatSourceInput> | undefined {
  const {agentType, publicDataEnabled} = settings;

  if (!doesAgentTypeSupportSources(agentType)) {
    return undefined;
  }

  const sources: Array<ChatSourceInput> = [];

  if (doesAgentTypeSupportPublicData(agentType) && publicDataEnabled) {
    sources.push({type: 'public_data'});
  }

  const mcpServerIds = settings.selectedMcpServerIds ?? fallbackMcpServerIds;
  for (const serverId of mcpServerIds) {
    sources.push({type: 'mcp_server', serverId});
  }

  return sources;
}

function getCitationGenerationMode(settings: ChatDraftSettings): CitationGenerationMode | undefined {
  if (settings.agentType !== AgentType.ASSISTANT) {
    return undefined;
  }
  return settings.citationGenerationMode ?? CitationGenerationMode.AUTO;
}

/**
 * Convert a draft state to the format needed for creating a display message chat event.
 *
 * @throws Error if settings is not defined on the draft
 */
export function getCreateDisplayMessageChatEvent(
  chatSessionId: string,
  draft: ChatDraftState,
  createParams?: CreateParams
): CreateDisplayMessageChatEvent {
  const {message, settings} = draft;
  if (!settings) {
    throw new Error(`Cannot create display message: settings not defined for chat session ${chatSessionId}`);
  }
  const contextIds = createParams?.contextIds;
  const fileUploads = getCompletedUploads(message.selectedFiles);
  return {
    chatSessionId,
    agentType: settings.agentType,
    messageBlocks: [
      {type: 'text', text: message.text},
      ...fileUploads.map((fileUpload) => ({
        type: 'file' as const,
        source: {type: 's3' as const, s3Location: fileUpload.s3Location},
        filename: fileUpload.filename,
        mediaType: fileUpload.mediaType,
      })),
    ],
    // TODO: userChatContext should eventually come from the draft
    userChatContext: getUserChatContext({
      contextIds: contextIds ?? getContextIdsFromActiveWorkspace(settings.agentType),
    }),
    sources: getSources(settings, createParams?.mcpServerIds ?? []),
    citationGenerationMode: getCitationGenerationMode(settings),
    retry: createParams?.retry,
    agentApprovalDecision: createParams?.agentApprovalDecision,
  };
}

// ============================================
// Global Client State
// ============================================

const ChatDraftGlobalClientState = makeUseGlobalClientState<ChatDraftState | null>(null);

// ============================================
// Key helpers
// ============================================

/**
 * Get the draft key for a chat. Uses NEW_CHAT_DRAFT_KEY for new chats (null chatSessionId).
 */
function getDraftKey(chatSessionId: string | null): string {
  return chatSessionId ?? NEW_CHAT_DRAFT_KEY;
}

// ============================================
// Non-reactive getters/setters (for use outside components)
// ============================================

/**
 * Set or clear the draft state for a chat.
 *
 * @package - ChatActions is the public interface for doing writes.
 */
export function setDraftState(chatSessionId: string | null, state: ChatDraftState | null): void {
  ChatDraftGlobalClientState.updateState(getDraftKey(chatSessionId), state);
}

/**
 * Get the current draft state for a chat.
 */
export function getDraftState(chatSessionId: string | null): ChatDraftState | null {
  return ChatDraftGlobalClientState.getState(getDraftKey(chatSessionId));
}

/**
 * Build the default draft state for a chat.
 * - For new chats: includes the default agent type so the input is immediately usable
 * - For existing chats: settings is undefined until seeded by useMessageThread
 */
function getDefaultDraftState(chatSessionId: string | null): ChatDraftState {
  const isNewChat = chatSessionId == null;
  const defaultAgentType = isNewChat ? getDefaultAgentType() : null;
  return {
    status: ChatDraftStatus.DRAFT,
    message: EMPTY_MESSAGE,
    failedUserMessage: undefined,
    settings: defaultAgentType
      ? {agentType: defaultAgentType, publicDataEnabled: getPublicDataEnabledPreference()}
      : undefined,
    agentTypeError: undefined,
  };
}

/**
 * Get the draft state, creating one with defaults if it doesn't exist.
 *
 * This handles the case where imperative code (e.g., selectFilesForDraft on file drop)
 * needs the draft before it has been written to global state. The created draft uses
 * getDefaultDraftState, so it matches the fallback that useChatDraftState computes
 * for rendering.
 */
export function getOrCreateDraftState(chatSessionId: string | null): ChatDraftState {
  const existing = getDraftState(chatSessionId);
  if (existing) {
    return existing;
  }

  const newState = getDefaultDraftState(chatSessionId);
  setDraftState(chatSessionId, newState);
  return newState;
}

/**
 * Clear the message content from a draft but preserve settings (agentType, publicDataEnabled).
 * Use this after successful submission to keep the user's settings for their next message.
 */
export function clearDraftMessageContent(chatSessionId: string | null): void {
  const state = getDraftState(chatSessionId);
  if (state) {
    setDraftState(chatSessionId, {
      ...state,
      status: ChatDraftStatus.DRAFT,
      message: {
        text: '',
        selectedFiles: [],
      },
      failedUserMessage: undefined,
      agentTypeError: undefined,
      replyToId: null,
      retryOfId: null,
    });
  }
}

/**
 * Seed the draft state with settings from a loaded message.
 *
 * This function fills in settings while preserving user's work:
 * - If no draft exists, creates a new draft with the settings
 * - If a draft exists but settings is undefined, fills it in
 * - User's text and files are always preserved
 */
export function seedDraftStateFromMessage(
  chatSessionId: string | null,
  newSettings: ChatDraftSettings
): void {
  const state = getDraftState(chatSessionId);

  if (!state) {
    // No draft exists - create a new one with the settings
    setDraftState(chatSessionId, {
      status: ChatDraftStatus.DRAFT,
      message: EMPTY_MESSAGE,
      failedUserMessage: undefined,
      settings: newSettings,
      agentTypeError: undefined,
    });
    return;
  }

  if (state.settings != null) {
    // Settings already exist - nothing to fill in
    return;
  }

  setDraftState(chatSessionId, {
    ...state,
    settings: newSettings,
  });
}

// ============================================
// File Management
// ============================================

/**
 * Replace a file in the draft with a new version.
 */
export function replaceFileInDraft(
  chatSessionId: string | null,
  key: string,
  newFile: ChatSelectedFile
): void {
  const state = getDraftState(chatSessionId);
  if (!state) {
    return;
  }

  setDraftState(chatSessionId, {
    ...state,
    message: {
      ...state.message,
      selectedFiles: state.message.selectedFiles.map((f) => (f.key === key ? newFile : f)),
    },
  });
}

/**
 * Remove a file from the draft by key.
 */
export function removeFileFromDraft(chatSessionId: string | null, key: string): void {
  const state = getDraftState(chatSessionId);
  if (!state) {
    return;
  }

  setDraftState(chatSessionId, {
    ...state,
    message: {
      ...state.message,
      selectedFiles: state.message.selectedFiles.filter((f) => f.key !== key),
    },
  });
}

// ============================================
// Utility functions
// ============================================

/**
 * Check if a draft has meaningful message content (not just settings changes).
 * Used to determine if we should show draft indicators.
 */
export function hasMeaningfulDraft(state: ChatDraftState | null): boolean {
  if (!state || state.status !== ChatDraftStatus.DRAFT) {
    return false;
  }
  // Only consider it a meaningful draft if there's text or file content
  return state.message.text.trim().length > 0 || state.message.selectedFiles.length > 0;
}

/**
 * Get a preview of the draft message for display (e.g., in tooltips).
 * Truncates long messages.
 */
export function getDraftPreview(state: ChatDraftState | null, maxLength: number = 50): string | null {
  if (!hasMeaningfulDraft(state)) {
    return null;
  }
  const text = state!.message.text.trim();
  if (text.length === 0) {
    const fileCount = state!.message.selectedFiles.length;
    return `${fileCount} file${fileCount === 1 ? '' : 's'} attached`;
  }
  if (text.length <= maxLength) {
    return text;
  }
  return `${text.substring(0, maxLength)}...`;
}

// ============================================
// React hooks
// ============================================

/**
 * Hook to use the draft state for a specific chat with React subscription.
 * Always returns a draft state object (never null).
 */
export function useChatDraftState(
  chatSessionId: string | null
): [ChatDraftState, (state: ChatDraftState | null) => void] {
  const key = getDraftKey(chatSessionId);
  const [state, setState] = ChatDraftGlobalClientState.useGlobalClientState(key);
  const draftState = useMemo(() => state ?? getDefaultDraftState(chatSessionId), [state, chatSessionId]);
  return [draftState, setState];
}
